/**
 * Theme System Exports
 *
 * Unified theme system for all frontend applications.
 */

export { darkTheme, default as darkThemeDefault } from './darkTheme';
// Design Guidelines (Apple vs Material)
export {
  availableDesignGuidelines,
  type DesignGuideline,
  designGuidelineLabels,
  type GuidelineMotion,
  type GuidelineRadius,
  type GuidelineShadows,
  type GuidelineSpacing,
  type GuidelineSurface,
  type GuidelineTokens,
  getGuidelineTokens,
  getInitialDesignGuideline,
  getStoredDesignGuideline,
  storeDesignGuideline,
} from './designGuidelines';
// Themes
export { default as lightThemeDefault, lightTheme } from './lightTheme';
// Liquid Glass Token System
export {
  type FloatingActionClearanceKey,
  floatingActionClearance,
  getLiquidGlassMaterial,
  getLiquidGlassShadow,
  type LiquidGlassVariant,
  type LiquidRadiusKey,
  type LiquidShadowLevel,
  type LiquidSpacingKey,
  liquidGlass,
  liquidRadius,
  liquidShadows,
  liquidShadowsDark,
  liquidSpacing,
  type SurfaceRadiusKey,
  surfaceRadius,
} from './liquidGlass';
// Material Design 3 Token System
export {
  getMaterialShadow,
  getMaterialSurface,
  type MaterialRadiusKey,
  type MaterialShadowLevel,
  type MaterialSpacingKey,
  type MaterialSurfaceVariant,
  materialMotion,
  materialRadius,
  materialShadows,
  materialSpacing,
  materialSurface,
  materialTonalElevation,
} from './materialDesign';
// Material Design 3 Typography
export {
  materialFontFamilies,
  materialFontWeights,
  materialResponsiveTypography,
  materialTypographyVariants,
} from './materialTypography';
export {
  default as midnightThemeDefault,
  midnightTheme,
} from './midnightTheme';
// Palette
export {
  type AccentColor,
  accentColors,
  baseColors,
  type ChartColorPalette,
  type ChartThemeMode,
  chartColors,
  darkPalette,
  type EventTypeColor,
  eventTypeColors,
  getChartColors,
  getEventTypeColor,
  getNeonStyles,
  getProviderColor,
  gradients,
  lightPalette,
  midnightPalette,
  type NeonAccent,
  NeonAccentKey,
  neonAccents,
  type ProviderBrand,
  portalBrandColors,
  providerBrandColors,
  type SeverityColor,
  type StatusColor,
  severityColors,
  statusColors,
  twilightPalette,
} from './palette';
// Portal Colors (per-portal theming)
export {
  adminGoldPalette,
  businessBluePalette,
  type ColorScale,
  consumerGreenPalette,
  generatePortalCSSVariables,
  getAvailableTemplates,
  getDefaultTemplate,
  getPortalPalette,
  opsmatrixMintPalette,
  type PortalColorPalette,
  type PortalColorTemplate,
  type PortalType,
  portalColorTemplates,
  portalDefaultTemplates,
  purplePalette,
  rosePalette,
  slatePalette,
  tealPalette,
  templateDescriptions,
  templateLabels,
} from './portalColors';
// Shadows & Effects
export {
  borderShimmerKeyframes,
  createGlow,
  createNeonGlow,
  darkShadows,
  focusRings,
  glassEffects,
  // CSS keyframe string exports (for global injection)
  glowBreathingKeyframes,
  glowEffects,
  innerShadows,
  lightShadows,
  type NeonGlowAccent,
  type NeonGlowIntensity,
  neonFlickerKeyframes,
  neonGlow,
  shadowWithGlow,
} from './shadows';
// Transitions & Animations
export {
  durations,
  easings,
  interactionAnimations,
  motionPresets,
  muiTransitions,
  reducedMotionPresets,
  springPresets,
  transitionPresets,
} from './transitions';
export {
  default as twilightThemeDefault,
  twilightTheme,
} from './twilightTheme';
// Typography
export {
  createWebsiteTypography,
  fontFamilies,
  fontSizes,
  fontWeights,
  iconSizes,
  letterSpacings,
  lineHeights,
  responsiveTypography,
  typographyVariants,
} from './typography';

// ============================================================================
// Theme Types
// ============================================================================

export type ThemeMode = 'light' | 'dark' | 'midnight' | 'twilight';

export interface ThemeConfig {
  mode: ThemeMode;
  primaryColor?: string;
  secondaryColor?: string;
}

// ============================================================================
// Theme Utilities
// ============================================================================

import type { Theme } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { darkTheme } from './darkTheme';
import { type DesignGuideline, getGuidelineTokens } from './designGuidelines';
import { lightTheme } from './lightTheme';
import { midnightTheme } from './midnightTheme';
import { twilightTheme } from './twilightTheme';

/**
 * Get base theme by mode (Apple Liquid Glass — original themes)
 */
function getBaseTheme(mode: ThemeMode): Theme {
  switch (mode) {
    case 'dark':
      return darkTheme;
    case 'midnight':
      return midnightTheme;
    case 'twilight':
      return twilightTheme;
    case 'light':
    default:
      return lightTheme;
  }
}

/**
 * Apply guideline-specific overrides to a base theme.
 * For 'apple', returns the base theme unchanged.
 * For 'material', applies MD3 typography, border radii, shadow system, and component overrides.
 */
function applyGuideline(baseTheme: Theme, guideline: DesignGuideline): Theme {
  if (guideline === 'apple') {
    // Add guideline marker — safe to mutate since it's idempotent for 'apple'
    Object.assign(baseTheme, { designGuideline: 'apple' as const });
    return baseTheme;
  }

  const tokens = getGuidelineTokens(guideline);
  const isDark = baseTheme.palette.mode === 'dark';
  const shadows = isDark ? tokens.shadowsDark : tokens.shadows;

  return createTheme({
    ...baseTheme,
    designGuideline: 'material',
    typography: tokens.typography,
    shape: {
      borderRadius: tokens.radius.md,
    },
    shadows: [
      'none',
      shadows[1],
      shadows[2],
      shadows[2],
      shadows[3],
      shadows[3],
      shadows[3],
      shadows[3],
      shadows[4],
      shadows[4],
      shadows[4],
      shadows[4],
      shadows[4],
      shadows[4],
      shadows[4],
      shadows[4],
      shadows[5],
      shadows[5],
      shadows[5],
      shadows[5],
      shadows[5],
      shadows[5],
      shadows[5],
      shadows[5],
      shadows[6],
    ],
    components: {
      ...baseTheme.components,
      ...tokens.componentOverrides,
    },
  });
}

/**
 * Get theme by mode, optionally applying a design guideline.
 *
 * @param mode - Color mode (light, dark, midnight, twilight)
 * @param guideline - Design guideline ('apple' | 'material'). Defaults to 'apple' for backward compatibility.
 */
export function getTheme(mode: ThemeMode, guideline: DesignGuideline = 'apple'): Theme {
  const baseTheme = getBaseTheme(mode);
  return applyGuideline(baseTheme, guideline);
}

/**
 * Cycle through theme modes: light -> dark -> twilight -> midnight -> light
 */
export function cycleThemeMode(currentMode: ThemeMode): ThemeMode {
  switch (currentMode) {
    case 'light':
      return 'dark';
    case 'dark':
      return 'twilight';
    case 'twilight':
      return 'midnight';
    case 'midnight':
      return 'light';
    default:
      return 'light';
  }
}

/**
 * Toggle between light and dark mode (legacy - prefer cycleThemeMode)
 * @deprecated Use cycleThemeMode for three-theme support
 */
export function toggleThemeMode(currentMode: ThemeMode): ThemeMode {
  return currentMode === 'light' ? 'dark' : 'light';
}

/**
 * Get system preferred color scheme
 */
export function getSystemThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Get stored theme preference
 */
export function getStoredThemeMode(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('theme-mode');
  if (stored === 'light' || stored === 'dark' || stored === 'midnight' || stored === 'twilight') return stored;
  return null;
}

/**
 * Store theme preference
 */
export function storeThemeMode(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('theme-mode', mode);
}

/**
 * Get initial theme mode (stored > system > light)
 */
export function getInitialThemeMode(): ThemeMode {
  return getStoredThemeMode() ?? getSystemThemeMode();
}

/**
 * Theme mode display names
 */
export const themeModeLabels: Record<ThemeMode, string> = {
  light: 'Light',
  dark: 'Dark',
  twilight: 'Twilight',
  midnight: 'Midnight',
};

/**
 * All available theme modes
 */
export const availableThemeModes: ThemeMode[] = ['light', 'dark', 'twilight', 'midnight'];
